import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';

export const supportedLanguages = [
  { code: 'en', short: 'EN', label: 'English' },
  { code: 'pt', short: 'PT', label: 'Português' },
  { code: 'ar', short: 'AR', label: 'العربية' },
];

const resources = {
  en: {
    translation: {
      common: {
        home: 'Home', gallery: 'Gallery', store: 'Store', about: 'About', contact: 'Contact', blog: 'Journal',
        account: 'Account', cart: 'Bag', search: 'Search', searchPlaceholder: 'Search the collection',
        lightMode: 'Use light mode', darkMode: 'Use dark mode', menu: 'Open menu', close: 'Close menu',
        loading: 'Loading…', all: 'All', canvas: 'Canvas', original: 'Originals', viewDetails: 'View details',
        retry: 'Try again', language: 'Language', back: 'Back', readMore: 'Read more',
      },
      home: {
        eyebrow: 'Sacred text · contemporary expression',
        title: 'The art of interpreting the sacred.',
        subtitle: 'Original Islamic calligraphy shaped by contemplation, material and the quiet power of the written word.',
        explore: 'Enter the gallery', shop: 'Shop the collection', archive: 'A living archive',
        archiveCopy: 'Works made to invite attention before ownership. Explore the forms, meanings and details at your own pace.',
        featured: 'Selected works', featuredCopy: 'A considered view into the Tafsir Art studio.',
        story: 'Meaning, made visible',
        storyCopy: 'Tafsir Art unites the exegetical depth of the Quran with the universal language of art. Each work begins with reflection and becomes a lasting visual reminder.',
        ourStory: 'Read our story', viewAll: 'View the full gallery',
      },
      gallery: {
        eyebrow: 'A living archive of calligraphy', title: 'Art Gallery',
        subtitle: 'A quiet space to appreciate the work. Commerce stays in the Store.',
        searchResults: 'Results for “{{query}}”', empty: 'No artworks were found in this collection.',
        enter: 'Enter the work', detailEyebrow: 'The viewing room',
      },
      store: {
        eyebrow: 'Tafsir Art · editions and objects', title: 'The Store',
        subtitle: 'Original artworks first, followed by made-to-order pieces from the Printify studio.',
        originals: 'Original artworks', originalsCopy: 'Studio works, canvases and originals available for purchase.',
        printify: 'Printed editions', printifyCopy: 'Calligraphy translated into apparel, prints and everyday objects.',
        loadingPrints: 'Preparing printed editions…', noPrints: 'Printed editions are not available yet.',
        from: 'From', options: '{{count}} options', artwork: 'Studio artwork',
      },
      detail: {
        galleryEyebrow: 'Artwork detail', storeEyebrow: 'Store · original artwork', otherViews: 'Other views',
        description: 'The work', dimensions: 'Dimensions', frame: 'Frame options', quantity: 'Quantity',
        price: 'Price', inStock: '{{count}} in stock', outOfStock: 'Out of stock', noFrame: 'No frame',
        add: 'Add to bag', buy: 'Buy now', relatedGallery: 'Continue exploring', relatedStore: 'More from the studio',
        backGallery: 'Back to gallery', backStore: 'Back to store', unavailable: 'Artwork not found',
      },
      about: {
        eyebrow: 'Our purpose', title: 'Interpretation becomes image.',
        intro: 'Tafsir Art was born from a clear vision: to unite the exegetical depth of the Quran with the universal language of art.',
        originTitle: 'The origin and the mission', originCopy: 'Inspired by the invitation to observation and reflection, sacred words are translated into visual expressions that ask the viewer to pause, contemplate and return.',
        practiceTitle: 'A contemplative practice', practiceCopy: 'Every composition balances script, material and negative space. The result is not only decoration, but a daily encounter with meaning.',
        cta: 'Experience the work',
      },
      contact: {
        eyebrow: 'Begin a conversation', title: 'Contact the studio', intro: 'Ask about a work, shipping or a private commission.',
        reach: 'Studio details', send: 'Send a message', name: 'Your name', email: 'Email address', subject: 'Subject', message: 'Your message',
        submit: 'Send message', sending: 'Sending…', success: 'Your message has been sent.', response: 'We usually reply within 24–48 hours.', location: 'Johannesburg',
      },
      blog: { eyebrow: 'Notes from the studio', title: 'Journal', subtitle: 'Calligraphy, symbolism and the contemplative life of Islamic art.' },
      footer: {
        note: 'Islamic calligraphy, interpreted through contemporary art.', explore: 'Explore', studio: 'Studio',
        newsletter: 'Studio letters', newsletterCopy: 'New works, essays and occasional studio notes.', email: 'Email address', subscribe: 'Subscribe',
        rights: 'All rights reserved.',
      },
      admin: {
        label: 'Tafsir / Admin', title: 'Studio Console', online: 'Studio online', dashboard: 'Overview', artworks: 'Artworks',
        images: 'Images', orders: 'Orders', printify: 'Printify', settings: 'Settings', viewSite: 'View site', logout: 'Log out',
      },
    },
  },
  pt: {
    translation: {
      common: {
        home: 'Início', gallery: 'Galeria', store: 'Loja', about: 'Sobre', contact: 'Contacto', blog: 'Diário',
        account: 'Conta', cart: 'Sacola', search: 'Pesquisar', searchPlaceholder: 'Pesquisar na coleção',
        lightMode: 'Usar modo claro', darkMode: 'Usar modo escuro', menu: 'Abrir menu', close: 'Fechar menu',
        loading: 'A carregar…', all: 'Tudo', canvas: 'Tela', original: 'Originais', viewDetails: 'Ver detalhes',
        retry: 'Tentar novamente', language: 'Idioma', back: 'Voltar', readMore: 'Ler mais',
      },
      home: {
        eyebrow: 'Texto sagrado · expressão contemporânea', title: 'A arte de interpretar o sagrado.',
        subtitle: 'Caligrafia islâmica original moldada pela contemplação, pela matéria e pela força serena da palavra escrita.',
        explore: 'Entrar na galeria', shop: 'Visitar a loja', archive: 'Um arquivo vivo',
        archiveCopy: 'Obras criadas para convidar à atenção antes da compra. Explore as formas, os significados e os detalhes ao seu ritmo.',
        featured: 'Obras selecionadas', featuredCopy: 'Um olhar cuidadoso sobre o estúdio Tafsir Art.',
        story: 'O significado torna-se visível', storyCopy: 'A Tafsir Art une a profundidade exegética do Alcorão à linguagem universal da arte. Cada obra nasce da reflexão e torna-se um lembrete visual duradouro.',
        ourStory: 'Conheça a nossa história', viewAll: 'Ver toda a galeria',
      },
      gallery: {
        eyebrow: 'Um arquivo vivo de caligrafia', title: 'Galeria de Arte',
        subtitle: 'Um espaço tranquilo para apreciar as obras. A compra permanece na Loja.',
        searchResults: 'Resultados para “{{query}}”', empty: 'Nenhuma obra foi encontrada nesta coleção.',
        enter: 'Entrar na obra', detailEyebrow: 'Sala de contemplação',
      },
      store: {
        eyebrow: 'Tafsir Art · edições e objetos', title: 'A Loja',
        subtitle: 'Primeiro as obras originais, seguidas das peças produzidas sob encomenda pela Printify.',
        originals: 'Obras originais', originalsCopy: 'Obras de estúdio, telas e originais disponíveis para compra.',
        printify: 'Edições impressas', printifyCopy: 'Caligrafia aplicada a vestuário, impressões e objetos do quotidiano.',
        loadingPrints: 'A preparar as edições impressas…', noPrints: 'As edições impressas ainda não estão disponíveis.',
        from: 'Desde', options: '{{count}} opções', artwork: 'Obra de estúdio',
      },
      detail: {
        galleryEyebrow: 'Detalhes da obra', storeEyebrow: 'Loja · obra original', otherViews: 'Outras vistas',
        description: 'A obra', dimensions: 'Dimensões', frame: 'Opções de moldura', quantity: 'Quantidade', price: 'Preço',
        inStock: '{{count}} em stock', outOfStock: 'Esgotado', noFrame: 'Sem moldura', add: 'Adicionar à sacola', buy: 'Comprar agora',
        relatedGallery: 'Continuar a explorar', relatedStore: 'Mais obras do estúdio', backGallery: 'Voltar à galeria', backStore: 'Voltar à loja', unavailable: 'Obra não encontrada',
      },
      about: {
        eyebrow: 'O nosso propósito', title: 'A interpretação torna-se imagem.',
        intro: 'A Tafsir Art nasceu de uma visão clara: unir a profundidade exegética do Alcorão à linguagem universal da arte.',
        originTitle: 'A origem e a missão', originCopy: 'Inspiradas pelo convite à observação e à reflexão, as palavras sagradas são traduzidas em expressões visuais que convidam a parar, contemplar e regressar.',
        practiceTitle: 'Uma prática contemplativa', practiceCopy: 'Cada composição equilibra caligrafia, matéria e espaço negativo. O resultado não é apenas decoração, mas um encontro diário com o significado.', cta: 'Conhecer as obras',
      },
      contact: {
        eyebrow: 'Inicie uma conversa', title: 'Contacte o estúdio', intro: 'Pergunte sobre uma obra, envio ou encomenda personalizada.',
        reach: 'Detalhes do estúdio', send: 'Enviar uma mensagem', name: 'O seu nome', email: 'Endereço de email', subject: 'Assunto', message: 'A sua mensagem',
        submit: 'Enviar mensagem', sending: 'A enviar…', success: 'A sua mensagem foi enviada.', response: 'Normalmente respondemos em 24–48 horas.', location: 'Johannesburg',
      },
      blog: { eyebrow: 'Notas do estúdio', title: 'Diário', subtitle: 'Caligrafia, simbolismo e a vida contemplativa da arte islâmica.' },
      footer: {
        note: 'Caligrafia islâmica interpretada através da arte contemporânea.', explore: 'Explorar', studio: 'Estúdio',
        newsletter: 'Cartas do estúdio', newsletterCopy: 'Novas obras, ensaios e notas ocasionais do estúdio.', email: 'Endereço de email', subscribe: 'Subscrever', rights: 'Todos os direitos reservados.',
      },
      admin: {
        label: 'Tafsir / Admin', title: 'Painel do Estúdio', online: 'Estúdio online', dashboard: 'Visão geral', artworks: 'Obras', images: 'Imagens',
        orders: 'Encomendas', printify: 'Printify', settings: 'Definições', viewSite: 'Ver site', logout: 'Terminar sessão',
      },
    },
  },
  ar: {
    translation: {
      common: {
        home: 'الرئيسية', gallery: 'المعرض', store: 'المتجر', about: 'من نحن', contact: 'تواصل', blog: 'المجلة',
        account: 'الحساب', cart: 'الحقيبة', search: 'بحث', searchPlaceholder: 'ابحث في المجموعة', lightMode: 'الوضع الفاتح', darkMode: 'الوضع الداكن',
        menu: 'فتح القائمة', close: 'إغلاق القائمة', loading: 'جارٍ التحميل…', all: 'الكل', canvas: 'لوحات', original: 'أعمال أصلية',
        viewDetails: 'عرض التفاصيل', retry: 'حاول مرة أخرى', language: 'اللغة', back: 'رجوع', readMore: 'اقرأ المزيد',
      },
      home: {
        eyebrow: 'نص مقدس · تعبير معاصر', title: 'فنّ تأويل المقدّس.',
        subtitle: 'خط إسلامي أصيل تشكّله التأملات والخامة والقوة الهادئة للكلمة المكتوبة.', explore: 'ادخل المعرض', shop: 'تسوّق المجموعة',
        archive: 'أرشيف حي', archiveCopy: 'أعمال تدعوك إلى التأمل قبل الاقتناء. استكشف الأشكال والمعاني والتفاصيل على مهل.',
        featured: 'أعمال مختارة', featuredCopy: 'نظرة متأنية إلى مرسم تفسير آرت.', story: 'المعنى يصبح مرئياً',
        storyCopy: 'يجمع تفسير آرت بين عمق تفسير القرآن ولغة الفن العالمية. تبدأ كل قطعة بالتأمل وتصبح تذكيراً بصرياً دائماً.',
        ourStory: 'اقرأ قصتنا', viewAll: 'شاهد المعرض كاملاً',
      },
      gallery: {
        eyebrow: 'أرشيف حي لفن الخط', title: 'معرض الفن', subtitle: 'مساحة هادئة لتأمل الأعمال، بينما يبقى الشراء في المتجر.',
        searchResults: 'نتائج البحث عن «{{query}}»', empty: 'لم نعثر على أعمال في هذه المجموعة.', enter: 'ادخل إلى العمل',
        detailEyebrow: 'غرفة التأمل',
      },
      store: {
        eyebrow: 'تفسير آرت · أعمال وإصدارات', title: 'المتجر', subtitle: 'الأعمال الأصلية أولاً، ثم المنتجات المصنوعة حسب الطلب من برنتيفاي.',
        originals: 'الأعمال الأصلية', originalsCopy: 'أعمال المرسم واللوحات والأصول المتاحة للاقتناء.', printify: 'الإصدارات المطبوعة',
        printifyCopy: 'فن الخط على الملابس والمطبوعات والأشياء اليومية.', loadingPrints: 'نحضّر الإصدارات المطبوعة…',
        noPrints: 'الإصدارات المطبوعة غير متاحة حالياً.', from: 'ابتداءً من', options: '{{count}} خيارات', artwork: 'عمل من المرسم',
      },
      detail: {
        galleryEyebrow: 'تفاصيل العمل', storeEyebrow: 'المتجر · عمل أصلي', otherViews: 'مناظر أخرى', description: 'عن العمل', dimensions: 'الأبعاد',
        frame: 'خيارات الإطار', quantity: 'الكمية', price: 'السعر', inStock: '{{count}} متوفر', outOfStock: 'غير متوفر', noFrame: 'بدون إطار',
        add: 'أضف إلى الحقيبة', buy: 'اشترِ الآن', relatedGallery: 'واصل الاستكشاف', relatedStore: 'المزيد من المرسم',
        backGallery: 'العودة إلى المعرض', backStore: 'العودة إلى المتجر', unavailable: 'العمل غير موجود',
      },
      about: {
        eyebrow: 'غايتنا', title: 'حين يصبح التأويل صورة.', intro: 'وُلد تفسير آرت من رؤية واضحة: الجمع بين عمق تفسير القرآن ولغة الفن العالمية.',
        originTitle: 'الأصل والرسالة', originCopy: 'استلهاماً من الدعوة إلى النظر والتدبر، تتحول الكلمات المقدسة إلى تعبيرات بصرية تدعو المشاهد إلى التوقف والتأمل والعودة.',
        practiceTitle: 'ممارسة تأملية', practiceCopy: 'توازن كل لوحة بين الخط والخامة والفراغ. والنتيجة ليست زينة فحسب، بل لقاء يومي مع المعنى.', cta: 'استكشف الأعمال',
      },
      contact: {
        eyebrow: 'ابدأ الحوار', title: 'تواصل مع المرسم', intro: 'اسأل عن عمل أو شحن أو طلب خاص.', reach: 'بيانات المرسم', send: 'أرسل رسالة',
        name: 'الاسم', email: 'البريد الإلكتروني', subject: 'الموضوع', message: 'رسالتك', submit: 'إرسال الرسالة', sending: 'جارٍ الإرسال…',
        success: 'تم إرسال رسالتك.', response: 'نرد عادة خلال 24–48 ساعة.', location: 'مابوتو، موزمبيق · بموعد مسبق',
      },
      blog: { eyebrow: 'ملاحظات من المرسم', title: 'المجلة', subtitle: 'الخط والرمزية والحياة التأملية للفن الإسلامي.' },
      footer: {
        note: 'الخط الإسلامي برؤية فنية معاصرة.', explore: 'استكشف', studio: 'المرسم', newsletter: 'رسائل المرسم',
        newsletterCopy: 'أعمال ومقالات وأخبار مختارة من المرسم.', email: 'البريد الإلكتروني', subscribe: 'اشترك', rights: 'جميع الحقوق محفوظة.',
      },
      admin: {
        label: 'تفسير / الإدارة', title: 'لوحة المرسم', online: 'المرسم متصل', dashboard: 'نظرة عامة', artworks: 'الأعمال', images: 'الصور',
        orders: 'الطلبات', printify: 'برنتيفاي', settings: 'الإعدادات', viewSite: 'عرض الموقع', logout: 'تسجيل الخروج',
      },
    },
  },
};

const initialLanguage = typeof window !== 'undefined'
  ? localStorage.getItem('tafsir-language') || 'en'
  : 'en';

const i18n = i18next.createInstance();
i18n.init({
  resources,
  lng: initialLanguage,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState(initialLanguage);

  const changeLanguage = useCallback((nextLanguage) => {
    const next = supportedLanguages.some(({ code }) => code === nextLanguage) ? nextLanguage : 'en';
    i18n.changeLanguage(next);
    localStorage.setItem('tafsir-language', next);
    setLanguage(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const value = useMemo(() => ({
    language,
    isRTL: language === 'ar',
    changeLanguage,
    t: (key, options) => i18n.t(key, { lng: language, ...options }),
  }), [changeLanguage, language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within I18nProvider');
  return context;
};

export default i18n;
